import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Clock, User, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { hasSessionLead } from '../services/api';

// Preview configuration
const PREVIEW_START_TIME = 15 * 60; // 15 minutes in seconds (900)
const PREVIEW_END_TIME = 17 * 60;   // 17 minutes in seconds (1020)
const PREVIEW_DURATION = PREVIEW_END_TIME - PREVIEW_START_TIME; // 2 minutes

const HeroVideoPreview = ({ webinar }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showFallback, setShowFallback] = useState(false);
  const loopCheckIntervalRef = useRef(null);
  const currentWebinarIdRef = useRef(null);

  // Get speaker name with proper fallback
  const speakerName = webinar?.speaker?.name || 'Guest Speaker';

  // Handle Watch Now click - navigate to webinar page, open lead form only if needed
  const handleWatchNow = useCallback(() => {
    if (webinar?.webinar_id) {
      const alreadyUnlocked = hasSessionLead();
      const url = alreadyUnlocked
        ? `/webinar/${webinar.webinar_id}`
        : `/webinar/${webinar.webinar_id}?open_form=true`;
      navigate(url);
    }
  }, [navigate, webinar?.webinar_id]);

  // Toggle mute/unmute
  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.unmute();
        setIsMuted(false);
      } else {
        videoRef.current.mute();
        setIsMuted(true);
      }
    }
  }, [isMuted]);

  useEffect(() => {
    // Reset state when webinar changes
    if (webinar?.webinar_id !== currentWebinarIdRef.current) {
      setIsVideoLoaded(false);
      setShowFallback(false);
      setIsMuted(true);
      
      // Clean up previous video
      if (videoRef.current) {
        try {
          videoRef.current.pause();
          videoRef.current.remove();
        } catch (e) {
          // Ignore cleanup errors
        }
        videoRef.current = null;
      }
      
      // Clear loop interval
      if (loopCheckIntervalRef.current) {
        clearInterval(loopCheckIntervalRef.current);
        loopCheckIntervalRef.current = null;
      }
      
      currentWebinarIdRef.current = webinar?.webinar_id;
    }

    if (!webinar?.wistia_media_id || !containerRef.current) {
      setShowFallback(true);
      return;
    }

    const mediaId = webinar.wistia_media_id;

    // Load Wistia script
    const loadWistiaScript = () => {
      return new Promise((resolve) => {
        if (window.Wistia) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://fast.wistia.com/assets/external/E-v1.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = () => {
          setShowFallback(true);
          resolve();
        };
        document.head.appendChild(script);
      });
    };

    // Initialize Wistia player for hero preview
    const initHeroPlayer = async () => {
      try {
        await loadWistiaScript();

        if (!window.Wistia) {
          setShowFallback(true);
          return;
        }

        // Load media embed
        const embedScript = document.createElement('script');
        embedScript.src = `https://fast.wistia.com/embed/medias/${mediaId}.jsonp`;
        embedScript.async = true;
        embedScript.onerror = () => setShowFallback(true);
        document.head.appendChild(embedScript);

        // Wait for Wistia
        const checkWistia = setInterval(() => {
          try {
            if (window.Wistia && window._wq) {
              clearInterval(checkWistia);

              window._wq = window._wq || [];
              window._wq.push({
                id: mediaId,
                options: {
                  autoPlay: true,
                  muted: true,
                  controlsVisibleOnLoad: false,
                  playbar: false,
                  smallPlayButton: false,
                  bigPlayButton: false,
                  volumeControl: false,
                  fullscreenButton: false,
                  settingsControl: false,
                  playbackRateControl: false,
                  qualityControl: false,
                  silentAutoPlay: true,
                  endVideoBehavior: 'loop',
                  videoFoam: true,
                  fitStrategy: 'cover',
                  playerColor: '000000',
                  stillUrl: webinar?.thumbnail || '',
                },
                onReady: (video) => {
                  try {
                    if (!video) {
                      console.error('Hero video object is null');
                      setShowFallback(true);
                      return;
                    }
                    
                    videoRef.current = video;
                    setIsVideoLoaded(true);

                    // Get video duration to validate preview range
                    const duration = video.duration ? video.duration() : 0;
                    
                    if (!duration || duration <= 0) {
                      console.warn('Video duration not available');
                      setShowFallback(true);
                      return;
                    }
                    
                    // Adjust start time if video is shorter than expected
                    let actualStartTime = PREVIEW_START_TIME;
                    let actualEndTime = PREVIEW_END_TIME;
                    
                    if (duration < PREVIEW_END_TIME) {
                      // If video is shorter, use proportional times (25% - 35% of video)
                      actualStartTime = Math.floor(duration * 0.25);
                      actualEndTime = Math.min(actualStartTime + PREVIEW_DURATION, duration - 5);
                    }

                    // Start at preview start time
                    try {
                      video.time(actualStartTime);
                      video.mute();
                      video.play();
                    } catch (err) {
                      console.error('Error starting video playback:', err);
                    }

                    // Loop logic - check every second if we need to restart
                    loopCheckIntervalRef.current = setInterval(() => {
                      try {
                        if (video && video.time) {
                          const currentTime = video.time();
                          if (currentTime >= actualEndTime || currentTime < actualStartTime - 1) {
                            video.time(actualStartTime);
                          }
                        }
                      } catch (err) {
                        // Silently ignore loop errors
                      }
                    }, 1000);

                    // Handle video errors
                    try {
                      if (video.bind) {
                        video.bind('error', () => {
                          setShowFallback(true);
                        });
                      }
                    } catch (err) {
                      console.error('Error binding error handler:', err);
                    }
                  } catch (err) {
                    console.error('Error in hero onReady handler:', err);
                    setShowFallback(true);
                  }
                },
                onEmbedFailed: (error) => {
                  console.error('Hero video embed failed:', error);
                  setShowFallback(true);
                }
              });
            }
          } catch (err) {
            console.error('Error checking Wistia for hero:', err);
            clearInterval(checkWistia);
            setShowFallback(true);
          }
        }, 100);

        // Timeout for Wistia init
        setTimeout(() => {
          clearInterval(checkWistia);
          if (!videoRef.current) {
            setShowFallback(true);
          }
        }, 8000);
      } catch (error) {
        console.error('Error initializing hero player:', error);
        setShowFallback(true);
      }
    };

    initHeroPlayer().catch(err => {
      console.error('Error in initHeroPlayer:', err);
      setShowFallback(true);
    });

    // Cleanup
    return () => {
      if (loopCheckIntervalRef.current) {
        clearInterval(loopCheckIntervalRef.current);
      }
      if (videoRef.current) {
        try {
          videoRef.current.pause();
          videoRef.current.remove();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [webinar?.wistia_media_id]);

  // Fallback hero with thumbnail
  if (showFallback || !webinar) {
    return (
      <section className="relative w-full h-[70vh] min-h-[500px] max-h-[700px] overflow-hidden bg-gray-900" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={webinar?.thumbnail || 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1920&h=1080&fit=crop'}
            alt={webinar?.title || 'Featured Webinar'}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

        {/* Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              {webinar ? (
                <>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
                    {webinar.title}
                  </h1>
                  <div className="flex items-center gap-6 text-gray-300 mb-8">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      <span>{webinar.duration || '45 mins'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      <span>{speakerName}</span>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                    onClick={handleWatchNow}
                    data-testid="hero-watch-now-btn"
                  >
                    <Play className="mr-2 h-6 w-6" fill="white" />
                    Watch Now
                  </Button>
                </>
              ) : (
                <div className="animate-pulse">
                  <div className="h-12 bg-gray-700 rounded w-3/4 mb-4" />
                  <div className="h-6 bg-gray-700 rounded w-full mb-2" />
                  <div className="h-6 bg-gray-700 rounded w-2/3 mb-8" />
                  <div className="h-14 bg-gray-700 rounded w-48" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full h-[70vh] min-h-[500px] max-h-[700px] overflow-hidden bg-black" data-testid="hero-section">
      {/* Wistia Video Background */}
      <div 
        ref={containerRef} 
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: 'none' }}
      >
        <div
          className={`wistia_embed wistia_async_${webinar.wistia_media_id} videoFoam=true fitStrategy=cover`}
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        >
          &nbsp;
        </div>
      </div>

      {/* Loading State */}
      {!isVideoLoaded && (
        <div className="absolute inset-0 bg-gray-900">
          <img
            src={webinar.thumbnail || 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1920&h=1080&fit=crop'}
            alt={webinar.title}
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
          </div>
        </div>
      )}

      {/* Dark Gradient Overlay for Readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 pointer-events-none" />

      {/* Content Overlay */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            {/* Title */}
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight drop-shadow-lg"
              data-testid="hero-title"
            >
              {webinar.title}
            </h1>

            {/* Metadata Row */}
            <div className="flex items-center gap-6 text-gray-300 mb-8">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span className="text-base sm:text-lg">{webinar.duration || '45 mins'}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5" />
                <span className="text-base sm:text-lg">{speakerName}</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-4">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                onClick={handleWatchNow}
                data-testid="hero-watch-now-btn"
              >
                <Play className="mr-2 h-6 w-6" fill="white" />
                Watch Now
              </Button>

              {/* Mute/Unmute Toggle */}
              {isVideoLoaded && (
                <Button
                  variant="outline"
                  size="icon"
                  className="w-12 h-12 rounded-full border-white/50 bg-black/30 hover:bg-black/50 text-white"
                  onClick={toggleMute}
                  data-testid="hero-mute-toggle"
                >
                  {isMuted ? (
                    <VolumeX className="h-5 w-5" />
                  ) : (
                    <Volume2 className="h-5 w-5" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade to Brand Blue */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0c1346] to-transparent pointer-events-none" />
    </section>
  );
};

export default HeroVideoPreview;
